import {
  angleInArc,
  arcBox,
  boxExpand,
  boxFromPoints,
  boxOf,
  boxUnion,
  closestPointOnSegment,
  dist,
  flattenArc,
  mirror,
  pt,
  rotate,
  scaleAbout,
  segmentIntersectsBox,
  type Box,
  type Pt,
} from './geom';
import { normalizeAngle, type Micron } from './units';

/**
 * 도면 엔티티 (작업지시서 P2-01)
 *
 * 판별 유니온으로 정의한다. 새 엔티티 종류를 추가하면 아래 4개 함수의
 * switch 가 컴파일 에러를 내므로, 구현을 빠뜨릴 수 없다:
 *   entityBox / entityHit / entitySnapPoints / transformEntity
 *
 * 좌표는 전부 정수 µm. 각도는 라디안.
 */

export type EntityType =
  | 'line'
  | 'polyline'
  | 'circle'
  | 'arc'
  | 'text'
  | 'blockref'
  | 'dimension';

export interface EntityCommon {
  id: string;
  /** 레이어 코드 (cad_layer_template.code). 색·선종류의 기본값 출처. */
  layer: string;
  /** null/undefined = ByLayer (레이어 색을 따름) */
  color?: string | null;
  lineType?: string | null;
  /** 1/100 mm. null = ByLayer */
  lineWeight?: number | null;
}

export interface LineEntity extends EntityCommon {
  type: 'line';
  a: Pt;
  b: Pt;
}

export interface PolylineEntity extends EntityCommon {
  type: 'polyline';
  points: Pt[];
  closed: boolean;
}

export interface CircleEntity extends EntityCommon {
  type: 'circle';
  center: Pt;
  radius: Micron;
}

export interface ArcEntity extends EntityCommon {
  type: 'arc';
  center: Pt;
  radius: Micron;
  /** 반시계 방향 start → end */
  startAngle: number;
  endAngle: number;
}

export type TextAlign = 'left' | 'center' | 'right';

export interface TextEntity extends EntityCommon {
  type: 'text';
  at: Pt;
  text: string;
  /** 글자 높이 (µm) */
  height: Micron;
  rotation: number;
  align: TextAlign;
}

export interface BlockRefEntity extends EntityCommon {
  type: 'blockref';
  blockCode: string;
  at: Pt;
  rotation: number;
  /** 균등 배율. 매대는 파라메트릭으로 처리하므로 비균등 배율은 쓰지 않는다. */
  scale: number;
  /** 블록 속성 (매대번호, 구역코드 등) */
  attributes: Record<string, string>;
}

export type DimKind = 'linear' | 'aligned' | 'radius' | 'angular';

export interface DimensionEntity extends EntityCommon {
  type: 'dimension';
  kind: DimKind;
  a: Pt;
  b: Pt;
  /** 치수선이 놓이는 위치 */
  offsetPoint: Pt;
  /** 비우면 실측값을 자동 표기 */
  overrideText?: string | null;
  textHeight: Micron;
}

export type CadEntity =
  | LineEntity
  | PolylineEntity
  | CircleEntity
  | ArcEntity
  | TextEntity
  | BlockRefEntity
  | DimensionEntity;

/* -------------------------------------------------------------------------- *
 * 블록 정의
 * -------------------------------------------------------------------------- */

export interface BlockDef {
  code: string;
  name: string;
  /** 블록 내부 도형. 좌표는 블록 원점(삽입점) 기준 µm. */
  entities: CadEntity[];
  /** 이격 요구 (mm). 통로 폭 검증(P5-02)에서 사용. */
  clearance?: { frontMm?: number; sideMm?: number; rearMm?: number };
  widthMm?: number;
  depthMm?: number;
}

/* -------------------------------------------------------------------------- *
 * 경계 상자
 * -------------------------------------------------------------------------- */

export function entityBox(e: CadEntity, blocks?: Map<string, BlockDef>): Box {
  switch (e.type) {
    case 'line':
      return boxFromPoints(e.a, e.b);

    case 'polyline':
      return boxOf(e.points);

    case 'circle':
      return {
        minX: e.center.x - e.radius,
        minY: e.center.y - e.radius,
        maxX: e.center.x + e.radius,
        maxY: e.center.y + e.radius,
      };

    case 'arc':
      return arcBox(e.center, e.radius, e.startAngle, e.endAngle);

    case 'text': {
      // 글꼴 메트릭 없이 근사한다. 선택 판정에는 충분하다.
      const w = e.text.length * e.height * 0.6;
      const h = e.height;
      const corners: Pt[] = [
        { x: 0, y: 0 },
        { x: w, y: 0 },
        { x: w, y: h },
        { x: 0, y: h },
      ].map((c) => {
        const dx = e.align === 'center' ? -w / 2 : e.align === 'right' ? -w : 0;
        const local = pt(c.x + dx, c.y);
        return rotate({ x: e.at.x + local.x, y: e.at.y + local.y }, e.at, e.rotation);
      });
      return boxOf(corners);
    }

    case 'blockref': {
      const def = blocks?.get(e.blockCode);
      if (!def || def.entities.length === 0) {
        // 정의를 못 찾으면 자리표시자 상자. 화면에서 "정의 없음"으로 그린다.
        const half = 500_000; // 500mm
        return boxExpand({ minX: e.at.x, minY: e.at.y, maxX: e.at.x, maxY: e.at.y }, half);
      }
      let box: Box | null = null;
      for (const child of def.entities) {
        const b = entityBox(transformBlockChild(child, e), blocks);
        box = box ? boxUnion(box, b) : b;
      }
      return box ?? { minX: e.at.x, minY: e.at.y, maxX: e.at.x, maxY: e.at.y };
    }

    case 'dimension':
      return boxOf([e.a, e.b, e.offsetPoint]);
  }
}

/** 블록 내부 엔티티를 삽입 변환(배율 → 회전 → 이동) 적용해 월드 좌표로 */
export function transformBlockChild(child: CadEntity, ref: BlockRefEntity): CadEntity {
  const toWorld = (p: Pt): Pt =>
    pt(
      ref.at.x + (p.x * Math.cos(ref.rotation) - p.y * Math.sin(ref.rotation)) * ref.scale,
      ref.at.y + (p.x * Math.sin(ref.rotation) + p.y * Math.cos(ref.rotation)) * ref.scale
    );
  return mapEntityPoints(child, toWorld, ref.rotation, ref.scale);
}

/* -------------------------------------------------------------------------- *
 * 히트 테스트 (클릭 선택)
 * -------------------------------------------------------------------------- */

/**
 * 점이 엔티티 위에 있는가.
 * `tol` 은 화면 픽셀을 월드 µm 로 환산한 값(조리개). 줌 배율과 무관하게
 * 일정한 클릭 감도를 주기 위해 호출자가 계산해서 넘긴다.
 */
export function entityHit(
  e: CadEntity,
  p: Pt,
  tol: Micron,
  blocks?: Map<string, BlockDef>
): boolean {
  switch (e.type) {
    case 'line':
      return dist(p, closestPointOnSegment(p, e.a, e.b)) <= tol;

    case 'polyline': {
      const n = e.points.length;
      for (let i = 0; i + 1 < n; i++) {
        if (dist(p, closestPointOnSegment(p, e.points[i]!, e.points[i + 1]!)) <= tol) return true;
      }
      if (e.closed && n > 2) {
        if (dist(p, closestPointOnSegment(p, e.points[n - 1]!, e.points[0]!)) <= tol) return true;
      }
      return false;
    }

    case 'circle': {
      // 테두리 위인지 본다. 채우기 없는 CAD 관례.
      const d = dist(p, e.center);
      return Math.abs(d - e.radius) <= tol;
    }

    case 'arc': {
      const d = dist(p, e.center);
      if (Math.abs(d - e.radius) > tol) return false;
      return angleInArc(Math.atan2(p.y - e.center.y, p.x - e.center.x), e.startAngle, e.endAngle);
    }

    case 'text':
    case 'dimension':
      return pointInBox(p, boxExpand(entityBox(e, blocks), tol));

    case 'blockref': {
      const def = blocks?.get(e.blockCode);
      if (!def) return pointInBox(p, boxExpand(entityBox(e, blocks), tol));
      // 블록은 내부 도형 중 하나라도 맞으면 선택된다.
      for (const child of def.entities) {
        if (entityHit(transformBlockChild(child, e), p, tol, blocks)) return true;
      }
      return false;
    }
  }
}

function pointInBox(p: Pt, b: Box): boolean {
  return p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY;
}

/**
 * Crossing 선택(우→좌 드래그) 판정 — 상자에 "걸치기만" 해도 선택.
 * Window 선택(좌→우)은 경계 상자 포함 여부로 충분하므로 호출자가 처리한다.
 */
export function entityCrossesBox(
  e: CadEntity,
  box: Box,
  blocks?: Map<string, BlockDef>
): boolean {
  switch (e.type) {
    case 'line':
      return segmentIntersectsBox(e.a, e.b, box);

    case 'polyline': {
      const n = e.points.length;
      for (let i = 0; i + 1 < n; i++) {
        if (segmentIntersectsBox(e.points[i]!, e.points[i + 1]!, box)) return true;
      }
      if (e.closed && n > 2) return segmentIntersectsBox(e.points[n - 1]!, e.points[0]!, box);
      return false;
    }

    case 'circle': {
      const seg = flattenArc(e.center, e.radius, 0, Math.PI * 2, e.radius / 20);
      for (let i = 0; i + 1 < seg.length; i++) {
        if (segmentIntersectsBox(seg[i]!, seg[i + 1]!, box)) return true;
      }
      return false;
    }

    case 'arc': {
      const seg = flattenArc(e.center, e.radius, e.startAngle, e.endAngle, e.radius / 20);
      for (let i = 0; i + 1 < seg.length; i++) {
        if (segmentIntersectsBox(seg[i]!, seg[i + 1]!, box)) return true;
      }
      return false;
    }

    case 'text':
    case 'dimension':
    case 'blockref': {
      const b = entityBox(e, blocks);
      return !(b.maxX < box.minX || b.minX > box.maxX || b.maxY < box.minY || b.minY > box.maxY);
    }
  }
}

/* -------------------------------------------------------------------------- *
 * 객체 스냅점 (OSNAP)
 * -------------------------------------------------------------------------- */

export type SnapKind =
  | 'endpoint'
  | 'midpoint'
  | 'center'
  | 'quadrant'
  | 'intersection'
  | 'perpendicular'
  | 'nearest'
  | 'insertion';

export interface SnapCandidate {
  kind: SnapKind;
  point: Pt;
  entityId: string;
}

/** 엔티티가 제공하는 스냅점들. 교차점(intersection)은 엔티티 쌍이 필요하므로 별도. */
export function entitySnapPoints(e: CadEntity, blocks?: Map<string, BlockDef>): SnapCandidate[] {
  const out: SnapCandidate[] = [];
  const push = (kind: SnapKind, point: Pt) => out.push({ kind, point, entityId: e.id });

  switch (e.type) {
    case 'line':
      push('endpoint', e.a);
      push('endpoint', e.b);
      push('midpoint', pt((e.a.x + e.b.x) / 2, (e.a.y + e.b.y) / 2));
      break;

    case 'polyline': {
      const n = e.points.length;
      for (const p of e.points) push('endpoint', p);
      for (let i = 0; i + 1 < n; i++) {
        const a = e.points[i]!;
        const b = e.points[i + 1]!;
        push('midpoint', pt((a.x + b.x) / 2, (a.y + b.y) / 2));
      }
      if (e.closed && n > 2) {
        const a = e.points[n - 1]!;
        const b = e.points[0]!;
        push('midpoint', pt((a.x + b.x) / 2, (a.y + b.y) / 2));
      }
      break;
    }

    case 'circle':
      push('center', e.center);
      // 사분점 — AutoCAD 사용자가 자주 쓴다
      push('quadrant', pt(e.center.x + e.radius, e.center.y));
      push('quadrant', pt(e.center.x, e.center.y + e.radius));
      push('quadrant', pt(e.center.x - e.radius, e.center.y));
      push('quadrant', pt(e.center.x, e.center.y - e.radius));
      break;

    case 'arc': {
      push('center', e.center);
      push('endpoint', pt(
        e.center.x + Math.cos(e.startAngle) * e.radius,
        e.center.y + Math.sin(e.startAngle) * e.radius
      ));
      push('endpoint', pt(
        e.center.x + Math.cos(e.endAngle) * e.radius,
        e.center.y + Math.sin(e.endAngle) * e.radius
      ));
      let sweep = normalizeAngle(e.endAngle) - normalizeAngle(e.startAngle);
      if (sweep <= 0) sweep += Math.PI * 2;
      const mid = normalizeAngle(e.startAngle) + sweep / 2;
      push('midpoint', pt(
        e.center.x + Math.cos(mid) * e.radius,
        e.center.y + Math.sin(mid) * e.radius
      ));
      break;
    }

    case 'text':
      push('insertion', e.at);
      break;

    case 'blockref': {
      push('insertion', e.at);
      const def = blocks?.get(e.blockCode);
      if (def) {
        for (const child of def.entities) {
          for (const s of entitySnapPoints(transformBlockChild(child, e), blocks)) {
            out.push({ ...s, entityId: e.id });
          }
        }
      }
      break;
    }

    case 'dimension':
      push('endpoint', e.a);
      push('endpoint', e.b);
      break;
  }
  return out;
}

/** 엔티티를 선분 열로 근사. 교차 스냅·TRIM·Crossing 판정의 공통 기반. */
export function entitySegments(e: CadEntity, blocks?: Map<string, BlockDef>): [Pt, Pt][] {
  const segs: [Pt, Pt][] = [];
  switch (e.type) {
    case 'line':
      segs.push([e.a, e.b]);
      break;
    case 'polyline': {
      const n = e.points.length;
      for (let i = 0; i + 1 < n; i++) segs.push([e.points[i]!, e.points[i + 1]!]);
      if (e.closed && n > 2) segs.push([e.points[n - 1]!, e.points[0]!]);
      break;
    }
    case 'circle': {
      const f = flattenArc(e.center, e.radius, 0, Math.PI * 2, Math.max(50, e.radius / 40));
      for (let i = 0; i + 1 < f.length; i++) segs.push([f[i]!, f[i + 1]!]);
      break;
    }
    case 'arc': {
      const f = flattenArc(e.center, e.radius, e.startAngle, e.endAngle, Math.max(50, e.radius / 40));
      for (let i = 0; i + 1 < f.length; i++) segs.push([f[i]!, f[i + 1]!]);
      break;
    }
    case 'blockref': {
      const def = blocks?.get(e.blockCode);
      if (def) {
        for (const child of def.entities) {
          segs.push(...entitySegments(transformBlockChild(child, e), blocks));
        }
      }
      break;
    }
    case 'text':
    case 'dimension':
      break;
  }
  return segs;
}

/* -------------------------------------------------------------------------- *
 * 변환
 * -------------------------------------------------------------------------- */

/**
 * 엔티티의 모든 좌표에 함수를 적용한다.
 * 회전/배율은 각도·반지름·글자높이에도 영향을 주므로 별도 인자로 받는다.
 */
export function mapEntityPoints(
  e: CadEntity,
  f: (p: Pt) => Pt,
  addRotation = 0,
  scaleFactor = 1
): CadEntity {
  switch (e.type) {
    case 'line':
      return { ...e, a: f(e.a), b: f(e.b) };
    case 'polyline':
      return { ...e, points: e.points.map(f) };
    case 'circle':
      return { ...e, center: f(e.center), radius: Math.round(e.radius * scaleFactor) };
    case 'arc':
      return {
        ...e,
        center: f(e.center),
        radius: Math.round(e.radius * scaleFactor),
        startAngle: normalizeAngle(e.startAngle + addRotation),
        endAngle: normalizeAngle(e.endAngle + addRotation),
      };
    case 'text':
      return {
        ...e,
        at: f(e.at),
        rotation: normalizeAngle(e.rotation + addRotation),
        height: Math.round(e.height * scaleFactor),
      };
    case 'blockref':
      return {
        ...e,
        at: f(e.at),
        rotation: normalizeAngle(e.rotation + addRotation),
        scale: e.scale * scaleFactor,
      };
    case 'dimension':
      return {
        ...e,
        a: f(e.a),
        b: f(e.b),
        offsetPoint: f(e.offsetPoint),
        textHeight: Math.round(e.textHeight * scaleFactor),
      };
  }
}

export function translateEntity(e: CadEntity, dx: Micron, dy: Micron): CadEntity {
  return mapEntityPoints(e, (p) => ({ x: p.x + dx, y: p.y + dy }));
}

export function rotateEntity(e: CadEntity, center: Pt, angle: number): CadEntity {
  return mapEntityPoints(e, (p) => rotate(p, center, angle), angle, 1);
}

export function scaleEntity(e: CadEntity, center: Pt, k: number): CadEntity {
  return mapEntityPoints(e, (p) => scaleAbout(p, center, k), 0, k);
}

/**
 * 대칭 복사.
 * 반사는 회전과 달리 각도의 부호를 뒤집으므로 mapEntityPoints 로 처리할 수 없다.
 * 호는 시작/끝 각을 서로 바꿔야 방향(반시계)이 유지된다.
 */
export function mirrorEntity(e: CadEntity, a: Pt, b: Pt): CadEntity {
  const f = (p: Pt) => mirror(p, a, b);
  const axisAngle = Math.atan2(b.y - a.y, b.x - a.x);
  const reflect = (ang: number) => normalizeAngle(2 * axisAngle - ang);

  switch (e.type) {
    case 'line':
      return { ...e, a: f(e.a), b: f(e.b) };
    case 'polyline':
      return { ...e, points: e.points.map(f) };
    case 'circle':
      return { ...e, center: f(e.center) };
    case 'arc':
      // 반사로 방향이 뒤집히므로 start/end 를 교환한다.
      return {
        ...e,
        center: f(e.center),
        startAngle: reflect(e.endAngle),
        endAngle: reflect(e.startAngle),
      };
    case 'text':
      return { ...e, at: f(e.at), rotation: reflect(e.rotation) };
    case 'blockref':
      return { ...e, at: f(e.at), rotation: reflect(e.rotation) };
    case 'dimension':
      return { ...e, a: f(e.a), b: f(e.b), offsetPoint: f(e.offsetPoint) };
  }
}

/* -------------------------------------------------------------------------- *
 * 그립 (작업지시서 P2-12)
 * -------------------------------------------------------------------------- */

/**
 * 그립 — 선택한 객체에 뜨는 파란 점. 끌어서 모양을 바꾼다.
 *
 * 매대 배치 실무에서 가장 많이 쓰는 조작이다. 명령을 치지 않고
 * 곤돌라 끝을 잡아 늘이거나 벽선을 붙이는 작업이 하루의 대부분이다.
 *
 * `move` 는 순수 함수다 — 새 엔티티를 돌려주며 원본을 건드리지 않는다.
 * 그래야 Undo(before/after)가 그대로 성립한다.
 */
export interface Grip {
  point: Pt;
  /** 이 그립을 새 위치로 옮긴 엔티티 */
  move: (to: Pt) => CadEntity;
}

export function entityGrips(e: CadEntity): Grip[] {
  switch (e.type) {
    case 'line':
      return [
        { point: e.a, move: (p) => ({ ...e, a: p }) },
        { point: e.b, move: (p) => ({ ...e, b: p }) },
      ];

    case 'polyline':
      return e.points.map((v, i) => ({
        point: v,
        move: (p: Pt) => ({
          ...e,
          points: e.points.map((q, j) => (j === i ? p : q)),
        }),
      }));

    case 'circle':
      return [
        // 중심 그립 = 이동
        { point: e.center, move: (p) => ({ ...e, center: p }) },
        // 사분점 그립 = 반지름 조정
        {
          point: pt(e.center.x + e.radius, e.center.y),
          move: (p) => ({ ...e, radius: Math.max(1, Math.round(dist(e.center, p))) }),
        },
      ];

    case 'arc':
      return [{ point: e.center, move: (p) => ({ ...e, center: p }) }];

    case 'text':
      return [{ point: e.at, move: (p) => ({ ...e, at: p }) }];

    case 'blockref':
      return [{ point: e.at, move: (p) => ({ ...e, at: p }) }];

    case 'dimension':
      return [
        { point: e.a, move: (p) => ({ ...e, a: p }) },
        { point: e.b, move: (p) => ({ ...e, b: p }) },
        { point: e.offsetPoint, move: (p) => ({ ...e, offsetPoint: p }) },
      ];
  }
}

/* -------------------------------------------------------------------------- *
 * 설명 문자열 (속성 패널 · 상태바)
 * -------------------------------------------------------------------------- */

export const ENTITY_LABEL: Record<EntityType, string> = {
  line: '선',
  polyline: '폴리선',
  circle: '원',
  arc: '호',
  text: '문자',
  blockref: '블록',
  dimension: '치수',
};
